/**
 * 課題管理の Slack 取込みの読み書き（oRPC `issueIngest` / `issueIngestWrite`）。
 *
 * 今すぐ取り込むは裏で動くので、最後の実行が実行中のあいだだけ状況を読み直し続ける。
 * 書き込みが成功したら取込みの状況・点検と、台帳の読み取り（声が増えた・戻った・本文を消した）を読み直す。
 *
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 課題管理の取込みチャンネルの状態・最後の取込み・月1回の点検を読み、今すぐ取り込む・チャンネルの追加と停止と招待した・本文削除・除外を戻す・点検を始める／終えるを書く
 * @graph-connects rpcClient [calls] issueIngest・issueIngestWrite の手続き
 */
import { type QueryKey, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { rpcClient } from '../lib/orpc.js';
import { queryKeys } from '../lib/query-keys.js';
import { ledgerStaleKeys } from './useIssueMgmt.js';

/**
 * 今すぐ取り込むが実行中のあいだ、状況を読み直す間隔。
 * @graph-connects none
 */
export const INGEST_RUNNING_POLL_MS = 5_000;

/**
 * 取込みの状況（チャンネルごとの状態・最後の取込み・前回の点検）。最後の取込みが実行中のあいだと、
 * 今すぐ取り込むで受け付けた実行（awaitRunId）が最後の取込みに出てくるまでのあいだは読み直し続ける
 * （受け付けてから動き出すまで間があり、その間は最後の取込みがまだ前の実行のため）。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 課題管理の取込みチャンネルの状態と最後の取込みの実行・前回の点検を取得し、今すぐ取り込むの結果が出るまで読み直す
 * @graph-connects rpcClient [calls] issueIngest.status
 * @graph-connects queryKeys [calls] Query key factory
 */
export function useIngestStatus(awaitRunId: string | null = null) {
  return useQuery({
    queryKey: queryKeys.issueMgmt.ingestStatus,
    queryFn: () => rpcClient.issueIngest.status(),
    refetchInterval: (query) => {
      const lastRun = query.state.data?.lastRun ?? null;
      const waiting = awaitRunId !== null && lastRun?.runId !== awaitRunId;
      return waiting || lastRun?.status === 'running' ? INGEST_RUNNING_POLL_MS : false;
    },
  });
}

/**
 * 最新の月1回の点検（抜き取った除外と声の行）。点検の一覧を開いたときだけ読む。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 最新の月1回の点検で抜き取った除外と声の行を取得する
 * @graph-connects rpcClient [calls] issueIngest.inspection
 * @graph-connects queryKeys [calls] Query key factory
 */
export function useInspection(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.issueMgmt.inspection,
    queryFn: () => rpcClient.issueIngest.inspection(),
    enabled,
  });
}

/**
 * 取込みの書き込みの手続き。
 * @graph-connects none
 */
type IngestWriteClient = typeof rpcClient.issueIngestWrite;

/**
 * 取込みの書き込みの手続き名。
 * @graph-connects none
 */
export type IngestWriteName = keyof IngestWriteClient;

/**
 * 取込みの書き込みのあとに読み直す読み取り（取込みの状況・点検・除外の一覧と台帳の読み取り）。
 * @graph-connects queryKeys [references] 読み直す読み取りのキー
 * @graph-connects ledgerStaleKeys [calls] 台帳の読み取り
 */
export function ingestStaleKeys(): QueryKey[] {
  return [
    queryKeys.issueMgmt.ingestStatus,
    queryKeys.issueMgmt.inspection,
    queryKeys.issueMgmt.excludedAll,
    ...ledgerStaleKeys(),
  ];
}

/**
 * 取込みの書き込み1つ。成功したら関係する読み取りの読み直しを投げる（終わるのは待たない）。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 課題管理の取込みの書き込みを1つ行い、成功したら取込みの状況・点検・除外の一覧・台帳の読み取りの読み直しを投げる
 * @graph-connects rpcClient [calls] issueIngestWrite の手続き
 * @graph-connects ingestStaleKeys [calls] 読み直す読み取り
 */
export function useIngestWrite<K extends IngestWriteName>(name: K) {
  const queryClient = useQueryClient();
  const procedure = rpcClient.issueIngestWrite[name] as (
    input: Parameters<IngestWriteClient[K]>[0],
  ) => Promise<Awaited<ReturnType<IngestWriteClient[K]>>>;
  return useMutation<
    Awaited<ReturnType<IngestWriteClient[K]>>,
    Error,
    Parameters<IngestWriteClient[K]>[0]
  >({
    mutationFn: (input) => procedure(input),
    onSuccess: () => {
      for (const queryKey of ingestStaleKeys()) {
        void queryClient.invalidateQueries({ queryKey });
      }
    },
  });
}
